import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Plus, FileText, AlertTriangle, Users, CheckCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ERPSite = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, reportsRes, issuesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/erp/site/stats`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/site/reports`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/site/issues`, { headers: getAuthHeader() })
      ]);
      setStats(statsRes.data);
      setReports(reportsRes.data);
      setIssues(issuesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Site Management</h1>
          <p className="text-gray-600 mt-1">Daily reports, issues, and worker attendance</p>
        </div>
        <Button className="bg-primary hover:bg-primary/90"><Plus className="w-4 h-4 mr-2" />New Report</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Site Reports</p><p className="text-2xl font-bold text-gray-900">{stats?.total_site_reports || 0}</p></div><FileText className="w-10 h-10 text-blue-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Total Issues</p><p className="text-2xl font-bold text-gray-900">{stats?.total_issues || 0}</p></div><AlertTriangle className="w-10 h-10 text-yellow-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Open Issues</p><p className="text-2xl font-bold text-orange-600">{stats?.open_issues || 0}</p></div><AlertTriangle className="w-10 h-10 text-orange-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Critical Issues</p><p className="text-2xl font-bold text-red-600">{stats?.critical_issues || 0}</p></div><AlertTriangle className="w-10 h-10 text-red-500" /></div></CardContent></Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card><div className="p-6"><h2 className="text-xl font-bold mb-4">Recent Site Reports</h2><div className="space-y-3">{reports.slice(0, 5).map(report => (<div key={report.id} className="p-3 bg-gray-50 rounded"><div className="flex justify-between items-start"><div><p className="font-medium">{new Date(report.date).toLocaleDateString()}</p><p className="text-sm text-gray-600">{report.work_completed}</p><p className="text-xs text-gray-500 mt-1">Workers: {report.workers_present} | Weather: {report.weather}</p></div></div></div>))}</div></div></Card>
        <Card><div className="p-6"><h2 className="text-xl font-bold mb-4">Active Issues</h2><div className="space-y-3">{issues.filter(i => i.status !== 'resolved').slice(0, 5).map(issue => (<div key={issue.id} className="p-3 bg-gray-50 rounded"><div className="flex justify-between items-start"><div><p className="font-medium">{issue.title}</p><p className="text-sm text-gray-600">{issue.description}</p><p className="text-xs text-gray-500 mt-1">{issue.category}</p></div><div className="flex flex-col items-end gap-1"><span className={`text-xs px-2 py-1 rounded ${issue.priority === 'critical' ? 'bg-red-100 text-red-700' : issue.priority === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>{issue.priority}</span><span className={`text-xs px-2 py-1 rounded ${issue.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{issue.status}</span></div></div></div>))}</div></div></Card>
      </div>
    </div>
  );
};

export default ERPSite;